import Phaser from '../../lib/phaser.js';
import { ATTACK_KEYS } from './attack-keys.js';
import { IceShard } from './ice-shard.js';
import { Slash } from './slash.js';

/**
 * @typedef {keyof typeof ATTACK_TARGET} AttackTarget
 */

/** @enum {AttackTarget} */
export const ATTACK_TARGET = Object.freeze({
  PLAYER: 'PLAYER',
  ENEMY: 'ENEMY',
});

/** Per-element tint applied to the strike animation so attacks stop looking identical. */
const ELEMENT_TINT = Object.freeze({
  flame: 0xff7a3d,
  frost: 0x8fe0ff,
  venom: 0xc774ff,
  tide: 0x4fa8ff,
  stone: 0xd2a86a,
  beast: 0x9be86b,
  storm: 0xffe14d,
  spirit: 0xff8fd0,
});

export class AttackManager {
  /** @type {Phaser.Scene} */
  #scene;
  /** @type {boolean} */
  #skipBattleAnimations;
  /** @type {IceShard} */
  #iceShardAttack;
  /** @type {Slash} */
  #slashAttack;

  /**
   * @param {Phaser.Scene} scene the Phaser 3 Scene the attack game object will be added to
   * @param {boolean} [skipBattleAnimations=false] used to skip all animations tied to the monster attack during battle
   */
  constructor(scene, skipBattleAnimations = false) {
    this.#scene = scene;
    this.#skipBattleAnimations = skipBattleAnimations;
  }

  /**
   * @param {import('./attack-keys.js').AttackKeys} attack
   * @param {AttackTarget} target
   * @param {() => void} callback
   * @returns {void}
   */
  playAttackAnimation(attack, target, callback, element) {
    const done = typeof callback === 'function' ? callback : () => {};
    if (this.#skipBattleAnimations) {
      done();
      return;
    }

    // if attack target is enemy
    let x = 745;
    let y = 140;
    if (target === ATTACK_TARGET.PLAYER) {
      x = 256;
      y = 344;
    }

    // Tint the strike by the attacker's element so every move doesn't look the same (0xffffff = none).
    const tint = ELEMENT_TINT[element] ?? 0xffffff;

    switch (attack) {
      case ATTACK_KEYS.ICE_SHARD:
        if (!this.#iceShardAttack) {
          this.#iceShardAttack = new IceShard(this.#scene, { x, y });
        }
        this.#iceShardAttack.gameObject.setPosition(x, y);
        this.#iceShardAttack.setTint(tint);
        this.#iceShardAttack.playAnimation(done);
        break;
      case ATTACK_KEYS.SLASH:
        if (!this.#slashAttack) {
          this.#slashAttack = new Slash(this.#scene, { x, y });
        }
        this.#slashAttack.gameObject.setPosition(x, y);
        this.#slashAttack.setTint(tint);
        this.#slashAttack.playAnimation(done);
        break;
      default:
        // CRITICAL: never throw on an unknown/undefined animationName. Earlier this called
        // exhaustiveGuard(attack), which threw and broke the attack→damage→callback chain,
        // leaving the battle permanently input-locked. The attacks roster can reference
        // animation names this manager doesn't render; in that case we simply skip the visual
        // and still drive the chain forward by invoking the completion callback.
        console.warn(`[attack-manager] no animation registered for "${attack}" — skipping visual`);
        done();
    }
  }
}
